import { type Currency, countryToCurrency } from '@/constants/biling';
import { useAuth } from '@/context/AuthContext';
import { useUnriddle } from '@/context/UnriddleContext';
import { useImpersonation } from '@/hooks/useImpersonation';
import useAnalytics from '@/lib/analytics/useAnalytics';
import { isAnaraTeamMember } from '@/lib/fingerprint';
import { api } from '@/lib/trpc/react';
import { prices } from '@/static/billingData';
import { getPriceId } from '@/utils/billingHelpers';
import { cn, toTitleCase } from '@/utils/utils';
import { useVisitorData } from '@fingerprintjs/fingerprintjs-pro-react';
import * as Sentry from '@sentry/nextjs';
import { atom, useAtom, useAtomValue, useSetAtom } from 'jotai';
import { Check } from 'lucide-react';
import { usePathname } from 'next/navigation';
import { useRouter } from 'next/router';
import { useEffect, useMemo, useState } from 'react';
import Balancer from 'react-wrap-balancer';
import { toast } from 'sonner';
import { match } from 'ts-pattern';
import { Button } from '../Button/Button';
import { LoadingSpinner } from '../Loading';
import { Dialog, DialogContent } from '../ui/Dialog';
import {
  type UpgradeReason,
  type UserPlan,
  featuresByPlan,
} from './UpgradeModal.utils';

// Atoms
const upgradeReasonAtom = atom<UpgradeReason>(null);

export const useUpgrade = () => {
  const showUpgrade = useSetAtom(upgradeReasonAtom);
  return { showUpgrade };
};

const PlanOptionSelect = ({
  currentPlan,
  setSelectedPlan,
  isSelected,
}: {
  currentPlan: { name: string; price: string };
  setSelectedPlan: () => void;
  isSelected: boolean;
}) => {
  return (
    <div
      onClick={setSelectedPlan}
      className={cn(
        'w-full text-left pl-3 pr-[23px] pt-2 pb-2.5 h-[62px] rounded-lg border transition-all flex flex-row items-center justify-between',
        isSelected
          ? 'border-badgeBlue/50 border-2 bg-[#017AFF05]'
          : 'border-border hover:border-muted-foreground/50',
      )}
    >
      <div className="flex flex-col">
        <span className="text-sm text-muted-foreground capitalize">
          {currentPlan.name}
        </span>
        <div className="flex items-center gap-1.5">
          <span className="text-base font-semibold">{currentPlan.price}</span>
          <span className="text-base font-medium text-muted-foreground">
            per month
          </span>
          {currentPlan.name === 'Yearly' && (
            <span className="px-1.5 text-xs py-0.5 bg-badgeLightBlue text-badgeBlue rounded-sm font-semibold">
              Save 40%
            </span>
          )}
        </div>
      </div>
      {isSelected && (
        <Check className="h-4 w-4 text-badgeBlue" strokeWidth={2.5} />
      )}
    </div>
  );
};

const UpgradeModal: React.FC = () => {
  const { user, userPlanDetails } = useAuth();
  const upgradeReason = useAtomValue(upgradeReasonAtom);
  const router = useRouter();
  const { isAdmin } = useImpersonation();
  const { track } = useAnalytics();
  const path = usePathname();

  const userPlan = useMemo((): UserPlan => {
    return (
      userPlanDetails ? toTitleCase(userPlanDetails.planName) : 'Free'
    ) as UserPlan;
  }, [userPlanDetails]);

  const inactiveMessage = useMemo(() => {
    if (!user?.subscription || user.subscription.length === 0) {
      return null;
    }

    return null;
  }, [user?.subscription]);

  const { mutateAsync: createPortal } = api.churnkey.portal.useMutation();

  const pageLimitCount = userPlan === 'Free' ? 120 : '10k';
  const sizeLimit = userPlan === 'Free' ? '20MB' : '300MB';
  const modelLanguage = userPlan === 'Free' ? 'the best AI' : 'Premium';

  const headline = useMemo(() => {
    return match(upgradeReason)
      .with('messageLimit', () => 'Upgrade for unlimited messages')
      .with('searchLimit', () => 'Upgrade for unlimited Ask AI messages')
      .with(
        'wordCountLimit',
        () => 'Upgrade for unlimited word generations in the editor',
      )
      .with('importLimit', () => 'Upgrade for unlimited imports')
      .with('recordingLimit', () => 'Upgrade for unlimited recordings')
      .with(
        'pageLimit',
        () => `Upgrade to import documents over ${pageLimitCount} pages`,
      )
      .with('sizeLimit', () =>
        userPlan === 'Free'
          ? `Upgrade to import documents over ${sizeLimit}`
          : 'Contact support to import documents over 300MB',
      )
      .with(
        'modelChange',
        () => `Upgrade for access to ${modelLanguage} models`,
      )
      .with('maxLengthChange', () => 'Upgrade to adjust the answer max length')
      .with(
        'teamsFeature',
        () => 'Collaborate with others by upgrading to Teams',
      )
      .with(
        'strategyFeature',
        () =>
          "We couldn't find any text in this document. Upgrade to import with OCR text detection.",
      )
      .with(
        'dailyPageLimit',
        () => 'Upgrade to import more than 600 pages per day',
      )
      .with('generic', () => 'Upgrade for unlimited access to Anara')
      .with(null, () => 'Upgrade for unlimited access to Anara')
      .exhaustive();
  }, [upgradeReason, pageLimitCount, sizeLimit, modelLanguage, userPlan]);

  const { userCountry } = useUnriddle();
  const { mutateAsync: createSubscription, isPending } =
    api.subscriptionRouter.create.useMutation();

  const [selectedPlan, setSelectedPlan] = useState<'yearly' | 'monthly'>(
    'yearly',
  );

  const getPlanPrice = (userCountry: Currency) => {
    const currency = countryToCurrency[userCountry] || 'USD';
    const planPrices = prices[currency].Pro;
    const symbol = prices[currency].symbol;
    return { ...planPrices, symbol, currency };
  };
  const [priceDetails, setPriceDetails] = useState({
    Monthly: {},
    Yearly: {},
    symbol: '',
    currency: '',
  } as {
    // biome-ignore lint/suspicious/noExplicitAny: <explanation>
    Monthly: any;
    // biome-ignore lint/suspicious/noExplicitAny: <explanation>
    Yearly: any;
    symbol: string;
    currency: string;
  });

  const { getData } = useVisitorData(
    { extendedResult: true },
    {
      immediate: false,
    },
  );
  const { checkAdmin } = useImpersonation();
  const { mutateAsync: accountByVisitorId } =
    api.fingerprint.accountByVisitorId.useMutation();

  useEffect(() => {
    const prices = getPlanPrice(userCountry);
    setPriceDetails(prices);
  }, [user, userCountry]);

  const purchasePlan = async () => {
    if (isAdmin || userPlan !== 'Free') {
      return;
    }

    track('paywall_banner_clicked', {
      reason: upgradeReason,
      selectedPlan: selectedPlan,
    });

    if (!isAnaraTeamMember(user?.email, user?.uniqueVisitorId)) {
      const verifiedData = await getData();

      if (
        verifiedData.visitorId &&
        !isAnaraTeamMember(user?.email, verifiedData.visitorId)
      ) {
        const accounts = await accountByVisitorId({
          visitorId: verifiedData.visitorId,
        });

        if (accounts.length >= 3 && !accounts.includes(user?.id ?? '')) {
          track('device_account_limit_reached', {
            from: 'upgrade_modal',
            userId: user?.id,
            visitorId: verifiedData.visitorId,
          });
          toast.error(
            <span>
              Maximum number of accounts for this device reached. Please{' '}
              <button
                className="cursor-pointer underline"
                id="intercom_support"
              >
                contact support
              </button>{' '}
              if this is a mistake.
            </span>,
            {
              duration: 7000,
            },
          );
          return;
        }
      } else {
        Sentry.captureMessage('No visitorId found');
      }
    }

    const priceId = getPriceId('pro', selectedPlan);

    if (!priceId) {
      return;
    }

    const url = await createSubscription({
      quantity: 1,
      priceId: priceId,
      referralId: null,
      plan: 'PRO',
      billingInterval: selectedPlan,
      successPath: path || 'new',
      cancelPath: path || 'new',
    });

    if (!url) {
      Sentry.captureException('No url returned from createSubscription');
      return;
    }

    window.location.href = url;
  };

  const features = useMemo(() => {
    return match(upgradeReason)
      .with('teamsFeature', () => [...featuresByPlan.Team])
      .otherwise(() => [...featuresByPlan.Pro]);
  }, [upgradeReason]);

  const showFeatures = !(upgradeReason === 'sizeLimit' && userPlan !== 'Free');
  const showPlanOptions = upgradeReason !== 'teamsFeature' && showFeatures;

  const handleBillingClick = async () => {
    if (!user?.id) return;
    const url = await createPortal();
    router.push(url);
  };

  const handlePricingClick = async () => {
    const admin = await checkAdmin(user?.email ?? '');
    if (admin) {
      return;
    }

    if (userPlan !== 'Free' && !inactiveMessage) {
      window.location.href = 'mailto:support@anara.com';
      return;
    }

    void (await purchasePlan());
  };

  return (
    <div
      className={'flex flex-col bg-popover w-[400px] rounded-xl h-full'}
      onClick={(e) => e.stopPropagation()}
    >
      <div className="px-5 py-4 h-full flex flex-col gap-5">
        <div className="">
          <h2 className="text-base font-semibold mb-2 mr-2">
            <Balancer>{headline}</Balancer>
          </h2>
          <p className="text-sm text-foreground/75 mb-4 pr-4">
            Invest in your success with a paid plan. Unlimited access, cancel
            anytime.
          </p>
          {showFeatures && (
            <ul className="text-sm space-y-1 leading-6 tracking-[-0.5%] pr-1">
              {features.map((feature, index) => (
                <li
                  key={`feature-${
                    // biome-ignore lint/suspicious/noArrayIndexKey: <explanation>
                    index
                  }`}
                  className="flex gap-2 items-start"
                >
                  <div className="flex-shrink-0 flex-grow-0 mt-[0.13rem]">
                    <Check strokeWidth={2.5} size={15} color="#ADADAD" />
                  </div>
                  <p>{feature.text}</p>
                </li>
              ))}
            </ul>
          )}
        </div>

        {showPlanOptions && (
          <div className="flex flex-col gap-2">
            <PlanOptionSelect
              currentPlan={{
                name: 'Yearly',
                price: `${priceDetails.symbol}${Math.ceil(priceDetails.Yearly.Price)} ${priceDetails.currency}`,
              }}
              setSelectedPlan={() => {
                setSelectedPlan('yearly');
              }}
              isSelected={selectedPlan === 'yearly'}
            />
            <PlanOptionSelect
              currentPlan={{
                name: 'Monthly',
                price: `${priceDetails.symbol}${Math.ceil(priceDetails.Monthly.Price)} ${priceDetails.currency}`,
              }}
              setSelectedPlan={() => {
                setSelectedPlan('monthly');
              }}
              isSelected={selectedPlan === 'monthly'}
            />
          </div>
        )}
        <div className="flex flex-col justify-stretch gap-1.5 pb-2 pt-3">
          <Button
            size="lg"
            disabled={isPending}
            onClick={inactiveMessage ? handleBillingClick : handlePricingClick}
          >
            {isPending ? (
              <LoadingSpinner variant="small" color="gray" />
            ) : (
              'Continue to checkout'
            )}
          </Button>
          {userPlan === 'Free' && (
            <span className="text-sm text-textGray4 text-center">
              Join 2M+ academics
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

const Upgrade = () => {
  const [upgradeReason, setUpgradeReason] = useAtom(upgradeReasonAtom);
  const { track } = useAnalytics();

  return (
    <Dialog
      open={!!upgradeReason}
      onOpenChange={(val) => {
        if (val === false) {
          setUpgradeReason(null);
          track('paywall_banner_closed');
        }
      }}
    >
      <DialogContent className="flex p-0 w-fit items-center justify-center rounded-xl">
        <UpgradeModal />
      </DialogContent>
    </Dialog>
  );
};

export default Upgrade;
